import React, { ReactNode, useEffect, useMemo } from 'react'
import {
  inspectorHoveredElementContext,
  inspectorContext,
  useInspectorOverlayEvents,
  InspectorContext,
  useHasAnyLayerWithExportableAssets,
} from '../../hooks/inspector'
import { useGetActiveSidebarTab } from '../../../components/ShareSidebarTabContext'
import { useSketchSceneTree } from '../../hooks/inspector/useSketchSceneTree'
import { isTopLevelFrameElement } from '../../../../inspector'

type FrameInspectorProviderProps = {
  children: ReactNode
  /**
   * Automatically select the top-level frame element when the inspector is active.
   * This can only work if the root is a top-level frame element (frame view).
   */
  autoSelect: boolean
}

export function FrameInspectorProvider({
  children,
  autoSelect,
}: FrameInspectorProviderProps) {
  const activeSidebarTab = useGetActiveSidebarTab()
  const isInspectorActive = activeSidebarTab === 'Inspect'

  const { sketchRootElement: sketchSceneRootElement, hasFailed } =
    useSketchSceneTree(isInspectorActive)

  const hasAnyLayerWithExportableAssets = useHasAnyLayerWithExportableAssets(
    sketchSceneRootElement
  )

  const {
    selectedElement,
    hoveredElement,
    setSelectedElement,
    setHoveredElement,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
  } = useInspectorOverlayEvents(
    sketchSceneRootElement,
    // Handle web renderer events only when we need to
    isInspectorActive
  )

  useEffect(() => {
    if (!isInspectorActive) {
      return
    }

    if (
      autoSelect &&
      sketchSceneRootElement &&
      isTopLevelFrameElement(sketchSceneRootElement)
    ) {
      setSelectedElement(sketchSceneRootElement)
    }

    /**
     * Reset the set element when the
     * inspector is disabled
     */
    return () => {
      setSelectedElement(null)
    }
  }, [
    setSelectedElement,
    isInspectorActive,
    autoSelect,
    sketchSceneRootElement,
  ])

  let inspectorStatus: InspectorContext['inspectorStatus'] = 'pending'

  if (sketchSceneRootElement) {
    inspectorStatus = 'ready' as const
  } else if (hasFailed) {
    inspectorStatus = 'errored' as const
  }

  const inspectorContextValue = useMemo((): InspectorContext => {
    return {
      selectedElement: selectedElement,
      isInspectorActive: isInspectorActive,
      sketchSceneRootElement: sketchSceneRootElement,
      setSelectedElement: setSelectedElement,
      setHoveredElement,
      inspectorStatus,
      hasAnyLayerWithExportableAssets,
      clickHandler,
      mouseMoveHandler,
      mouseDownHandle,
    }
  }, [
    clickHandler,
    hasAnyLayerWithExportableAssets,
    sketchSceneRootElement,
    isInspectorActive,
    mouseMoveHandler,
    selectedElement,
    setHoveredElement,
    setSelectedElement,
    inspectorStatus,
    mouseDownHandle,
  ])

  return (
    <inspectorContext.Provider value={inspectorContextValue}>
      <inspectorHoveredElementContext.Provider value={hoveredElement}>
        {children}
      </inspectorHoveredElementContext.Provider>
    </inspectorContext.Provider>
  )
}
